
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Star, 
  MessageSquare, 
  Send, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from "lucide-react";
import { format } from "date-fns";

interface SatisfactionSurvey {
  id: string;
  placementId: string;
  candidateName: string;
  position: string;
  sentDate: Date;
  completedDate?: Date;
  status: 'sent' | 'completed' | 'overdue';
  overallRating?: number;
  responses?: {
    candidateQuality: number;
    communicationRating: number;
    timelineSatisfaction: number;
    wouldRecommend: boolean;
    feedback: string;
  };
}

interface ClientSatisfactionTabProps {
  clientId: string;
  clientName: string;
}

// Mock data for satisfaction surveys
const mockSurveys: SatisfactionSurvey[] = [
  {
    id: '1',
    placementId: 'p1',
    candidateName: 'John Smith',
    position: 'Senior React Developer',
    sentDate: new Date('2024-01-10T10:00:00'),
    completedDate: new Date('2024-01-12T14:30:00'),
    status: 'completed',
    overallRating: 5,
    responses: {
      candidateQuality: 5,
      communicationRating: 4,
      timelineSatisfaction: 5,
      wouldRecommend: true,
      feedback: 'Excellent service! The candidate was exactly what we needed.'
    }
  },
  {
    id: '2',
    placementId: 'p2',
    candidateName: 'Sarah Johnson',
    position: 'Frontend Developer',
    sentDate: new Date('2024-01-20T09:00:00'),
    status: 'sent'
  },
  {
    id: '3',
    placementId: 'p3',
    candidateName: 'Mike Chen',
    position: 'DevOps Engineer',
    sentDate: new Date('2024-01-05T11:00:00'),
    status: 'overdue'
  }
];

export const ClientSatisfactionTab: React.FC<ClientSatisfactionTabProps> = ({ 
  clientId, 
  clientName 
}) => {
  const [surveys] = useState<SatisfactionSurvey[]>(mockSurveys);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'sent': return <Clock className="h-4 w-4" />;
      case 'overdue': return <AlertCircle className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const renderStarRating = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-muted-foreground">{rating}/5</span>
      </div>
    );
  };

  const completedSurveys = surveys.filter(s => s.status === 'completed');
  const averageRating = completedSurveys.length > 0 
    ? completedSurveys.reduce((sum, s) => sum + (s.overallRating || 0), 0) / completedSurveys.length 
    : 0;

  return (
    <div className="space-y-6">
      {/* Satisfaction Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageRating.toFixed(1)}</div>
            {renderStarRating(Math.round(averageRating))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Completed Surveys</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedSurveys.length}</div>
            <p className="text-xs text-muted-foreground">out of {surveys.length} sent</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {surveys.length > 0 ? Math.round((completedSurveys.length / surveys.length) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">completion rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overdue Surveys</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {surveys.filter(s => s.status === 'overdue').length}
            </div>
            <p className="text-xs text-muted-foreground">need follow-up</p>
          </CardContent>
        </Card>
      </div>

      {/* Survey Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Survey Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button className="h-16 flex flex-col items-center gap-2">
              <Send className="h-5 w-5" />
              <span className="text-sm">Send New Survey</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <span className="text-sm">Follow Up Overdue</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <span className="text-sm">View Analytics</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Surveys List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Satisfaction Surveys
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {surveys.map((survey) => (
              <div key={survey.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium">{survey.candidateName} - {survey.position}</h3>
                    <p className="text-sm text-muted-foreground">
                      Survey sent: {format(survey.sentDate, 'MMM d, yyyy')}
                    </p>
                    {survey.completedDate && (
                      <p className="text-sm text-muted-foreground">
                        Completed: {format(survey.completedDate, 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(survey.status)}>
                      {getStatusIcon(survey.status)}
                      <span className="ml-1 capitalize">{survey.status}</span>
                    </Badge>
                  </div>
                </div>

                {survey.responses && (
                  <div className="space-y-3 pt-3 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Overall Rating</p>
                        {renderStarRating(survey.overallRating!)}
                      </div>
                      <div>
                        <p className="text-sm font-medium">Would Recommend</p>
                        <p className="text-sm text-muted-foreground">
                          {survey.responses.wouldRecommend ? '✓ Yes' : '✗ No'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="font-medium">Candidate Quality</p>
                        {renderStarRating(survey.responses.candidateQuality)}
                      </div>
                      <div>
                        <p className="font-medium">Communication</p>
                        {renderStarRating(survey.responses.communicationRating)}
                      </div>
                      <div>
                        <p className="font-medium">Timeline</p>
                        {renderStarRating(survey.responses.timelineSatisfaction)}
                      </div>
                    </div>

                    {survey.responses.feedback && (
                      <div>
                        <p className="text-sm font-medium">Feedback</p>
                        <p className="text-sm text-muted-foreground bg-gray-50 p-3 rounded">
                          "{survey.responses.feedback}"
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {survey.status !== 'completed' && (
                  <div className="flex items-center gap-2 pt-2">
                    <Button size="sm" variant="outline">
                      <Send className="mr-2 h-4 w-4" />
                      Resend Survey
                    </Button>
                    {survey.status === 'overdue' && (
                      <Button size="sm" variant="outline">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Follow Up
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
